/**
 * FOH WORKBOARD — the front office's home screen.
 *
 * "Which advisor owns this RO, what needs attention next, and what are we
 * waiting on?" Six DERIVED lanes over one read model (useFohWorkboard →
 * deriveFohWorkboardLane). No drag and drop: a card moves because a fact
 * moved. The old FOH queue lives on as Needs Attention — same aging, urgency,
 * handoff reason, sender, note, Accept Ticket and Kick Back — fed by the same
 * read, so the two can never disagree.
 *
 * Not a second Command Center: a card opens the real one through the deep
 * link (which selects the ticket's own Dayboard day, or offers the fallback).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useFohWorkboard } from '@/hooks/foh/useFohWorkboard';
import { useSetTicketAdvisor } from '@/hooks/foh/useSetTicketAdvisor';
import { useFohCapableMembers } from '@/hooks/tickets/useFohCapableMembers';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { UNKNOWN_MEMBER_LABEL } from '@/lib/people/personDisplayName';
import { useTicketCustodyMutation } from '@/hooks/tickets/useTicketCustody';
import { buildCustodyHandoffMetadata } from '@/lib/tickets/lifecycle/custodyHandoffReasons';
import { CustodyHandoffReasonDialog } from '@/components/tickets/CustodyHandoffReasonDialog';
import { clock } from '@/utils/clock';
import {
  buildWorkboardLanes,
  FOH_LANE_LABELS,
  FOH_LANES,
  formatRoNumber,
  matchesAdvisorFilter,
  matchesWorkboardSearch,
  type AdvisorFilter,
  type FohLane,
  type WorkboardCard,
} from '@/lib/foh/workboard/workboardModel';
import { FohWorkboardCard } from './FohWorkboardCard';
import { AdvisorAssignDialog } from './AdvisorAssignDialog';
// Oct 20 (Issues 03A / 03B): vehicle actions on the Workboard, through the existing authorities only.
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { describeLocation } from '@/lib/location/labels';
import { workboardVehicleActions } from '@/lib/foh/workboard/workboardActions';
import { recordCompletedPickup, type PickupActFeedback } from '@/lib/foh/pickup/completedPickupAct';
import { recordIncompletePickup } from '@/lib/foh/pickup/incompletePickupAct';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { useOutstandingIncompletePickups } from '@/hooks/foh/useOutstandingIncompletePickups';
import { IncompletePickupDialog } from '@/components/tickets/IncompletePickupDialog';
import { ReturnCheckInDialog } from './ReturnCheckInDialog';
import { ReturnContinuationDialog } from './ReturnContinuationDialog';
import type { OutstandingIncompletePickup } from '@/hooks/foh/useOutstandingIncompletePickups';
import { FohMoveVehicleDialog } from './FohMoveVehicleDialog';
import { FohPickupConfirmDialog } from './FohPickupConfirmDialog';
import { OutstandingIncompletePickups } from './OutstandingIncompletePickups';

/** The pickup a person is confirming on the Workboard. */
type PendingPickup = { kind: 'completed' | 'incomplete'; ticketId: string; subject: string; whereNow: string | null; fromStatus: string | null };

/** The Command Center for a ticket — the existing deep link (day switch + fallback included). */
export const commandCenterPath = (ticketId: string) =>
  `/?tab=dayboard&highlight=${encodeURIComponent(ticketId)}&openDetails=true`;

const LANE_EMPTY: Record<FohLane, string> = {
  needs_attention: 'Nothing needs the front office right now.',
  waiting: 'Not waiting on anyone.',
  with_shop: 'Nothing with the shop.',
  closeout: 'Nothing to close out.',
  upcoming: 'Nothing scheduled ahead.',
  done_today: 'Nothing picked up yet today.',
};

export function FohWorkboard({ shopId }: { shopId: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const me = user?.id ?? null;
  const { shopTimezone } = useShopTimezone();
  const perms = usePermissions();
  const canAssign = !!perms.canReassignAdvisor;
  const leadsTheShop = !!(perms.isOwner || perms.isManager || perms.isOrgAdmin);

  const [filter, setFilter] = useState<AdvisorFilter>(leadsTheShop ? 'all' : 'mine');
  const [query, setQuery] = useState('');
  const [doneOpen, setDoneOpen] = useState(false);
  const [kickBackTicketId, setKickBackTicketId] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState<WorkboardCard | null>(null);
  const [movingTicketId, setMovingTicketId] = useState<string | null>(null);
  const [pickup, setPickup] = useState<PendingPickup | null>(null);
  const [returning, setReturning] = useState<{ row: OutstandingIncompletePickup; act: 'check_in' | 'schedule' } | null>(null);
  const [pickupBusy, setPickupBusy] = useState(false);
  const queryClient = useQueryClient();
  const outstanding = useOutstandingIncompletePickups(shopId);

  const board = useFohWorkboard(shopId, shopTimezone);
  const { data: members = [] } = useFohCapableMembers({ shopId });
  const custody = useTicketCustodyMutation();
  const advisor = useSetTicketAdvisor(shopId);

  const data = board.data;
  // Only REAL names here: a member without one must not mask a real name the
  // actor lookup finds (and the placeholder is applied at render time).
  const memberNames = useMemo(
    () => Object.fromEntries(members.filter((m) => m.displayName !== UNKNOWN_MEMBER_LABEL).map((m) => [m.userId, m.displayName])),
    [members]);
  const otherIds = useMemo(() => {
    if (!data) return [];
    const ids = [...data.handoffs.values()].map((h) => h.actorId);
    for (const ro of data.repairOrders) if (ro.advisorUserId && !memberNames[ro.advisorUserId]) ids.push(ro.advisorUserId);
    return ids;
  }, [data, memberNames]);
  const { data: actorNames } = useCustodyActorNames(otherIds, shopId);
  const names = useMemo(() => ({ ...(actorNames ?? {}), ...memberNames }), [actorNames, memberNames]);

  const nowIso = clock.now().toISOString();
  const visible = useMemo(() => (data?.repairOrders ?? []).filter((ro) => matchesWorkboardSearch(ro, query)), [data, query]);
  const lanes = useMemo(
    () => buildWorkboardLanes(visible.filter((ro) => matchesAdvisorFilter(ro, filter, me)),
      { today: data?.today ?? '', shopTimezone, nowIso }),
    // nowIso changes every render; aging is re-derived on each refetch instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, filter, me, data?.today, shopTimezone],
  );
  const onBoard = useMemo(() => buildWorkboardLanes(visible, { today: data?.today ?? '', shopTimezone, nowIso }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, data?.today, shopTimezone]);
  const activeCards = (l: Record<FohLane, WorkboardCard[]>) => FOH_LANES.flatMap((x) => l[x]);
  const mineCount = activeCards(onBoard).filter((c) => matchesAdvisorFilter(c.ro, 'mine', me)).length;
  const unassignedCount = activeCards(onBoard).filter((c) => c.ro.advisorUserId == null && c.placement.lane !== 'done_today').length;

  const filters: Array<{ key: AdvisorFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'My Tickets' },
    ...members.filter((m) => m.userId !== me).map((m) => ({ key: m.userId, label: m.displayName })),
    { key: 'unassigned', label: 'Unassigned' },
  ];

  const accept = (ticketId: string) => {
    custody.mutateAsync({ ticketId, eventKind: 'foh_accept', reason: null }).catch(() => { /* hook toasts */ });
  };
  const kickBack = (ticketId: string, result: { reason: string; reasonLabel: string; notes: string | null }) => {
    const metadata = buildCustodyHandoffMetadata({
      trigger: 'manual', reason: result.reason, reasonLabel: result.reasonLabel, notes: result.notes, sourceComponent: 'FohWorkboard',
    });
    custody.mutateAsync({ ticketId, eventKind: 'kick_to_boh', reason: result.reason, metadata })
      .then(() => setKickBackTicketId(null)).catch(() => { /* hook toasts */ });
  };

  const vehiclePerms = {
    canRelocateVehicles: !!perms.canRelocateVehicles,
    canReleaseCompletedVehicle: !!perms.canReleaseCompletedVehicle,
    canEditTickets: !!perms.canEditTickets,
  };
  const refreshBoard = () => queryClient.invalidateQueries({ queryKey: ['foh-custody-queue', shopId] });
  const say = (r: PickupActFeedback) => toast[r.tone](r.title, r.description ? { description: r.description } : undefined);
  const subjectOf = (card: WorkboardCard) => [formatRoNumber(card.ro.owner), card.ro.owner.customer_name].filter(Boolean).join(' · ');
  const whereOf = (card: WorkboardCard) => {
    const loc = data?.locations.get(card.ro.id) ?? null;
    return loc && data ? describeLocation(loc, data.locationNames).label : null;
  };
  const confirmPickup = async (reason?: string, expectedReturnDate?: string | null, releasePauseIds: string[] = []) => {
    if (!pickup || pickupBusy) return;
    setPickupBusy(true);
    try {
      const r = pickup.kind === 'completed'
        ? await recordCompletedPickup(pickup.ticketId, me)
        : await recordIncompletePickup({
          ticketId: pickup.ticketId, reason: reason ?? '', expectedReturnDate: expectedReturnDate ?? null, releasePauseIds,
          actorUserId: me, fromStatus: pickup.fromStatus, shopTimezone,
        });
      say(r);
      if (r.settled || r.reload) setPickup(null);
      await refreshBoard();
    } catch (e) {
      console.error('[FohWorkboard] pickup failed', e);
      toast.error('Pickup could not be recorded', { description: 'Reload the board and check the ticket before trying again.' });
    } finally {
      setPickupBusy(false);
    }
  };

  if (board.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading the Workboard…</div>;
  }
  if (board.error || !data) {
    return <div className="p-6 text-sm text-destructive" data-testid="foh-workboard-error">The Workboard could not be loaded. It will retry when tickets change.</div>;
  }

  const renderLane = (lane: FohLane) => {
    const cards = lanes[lane];
    const collapsible = lane === 'done_today';
    const open = !collapsible || doneOpen;
    return (
      <section key={lane} data-testid={`foh-lane-${lane}`} className="flex flex-col gap-2 md:min-w-[290px] md:max-w-[340px] md:flex-1">
        <button
          type="button"
          disabled={!collapsible}
          onClick={() => collapsible && setDoneOpen((v) => !v)}
          className="flex items-center gap-1.5 text-left text-sm font-semibold"
          data-testid={`foh-lane-header-${lane}`}
        >
          {collapsible && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
          {FOH_LANE_LABELS[lane]}
          <span className="rounded-full bg-muted px-2 text-xs font-medium" data-testid={`foh-lane-count-${lane}`}>{cards.length}</span>
        </button>
        {open && (cards.length === 0
          ? <p className="text-xs text-muted-foreground px-1">{LANE_EMPTY[lane]}</p>
          : cards.map((card) => (
            <FohWorkboardCard
              key={card.ro.id}
              card={card}
              handoff={data.handoffs.get(card.ro.id) ?? null}
              location={data.locations.get(card.ro.id) ?? null}
              locationNames={data.locationNames}
              names={names}
              shopTimezone={shopTimezone}
              today={data.today}
              nowIso={nowIso}
              canAssign={canAssign}
              busy={custody.isPending || advisor.isPending}
              onOpen={(id) => navigate(commandCenterPath(id))}
              onAccept={accept}
              onKickBack={setKickBackTicketId}
              onClaim={(id) => advisor.mutate({ ticketId: id, mode: 'claim' })}
              onReassign={setReassigning}
              vehicleActions={workboardVehicleActions(card.ro, vehiclePerms)}
              onMoveVehicle={(id) => setMovingTicketId(id)}
              onPickedUp={(id, c) => setPickup({ kind: 'completed', ticketId: id, subject: subjectOf(c), whereNow: whereOf(c), fromStatus: c.ro.live.status ?? null })}
              onPickedUpIncomplete={(id, c) => setPickup({ kind: 'incomplete', ticketId: id, subject: subjectOf(c), whereNow: whereOf(c), fromStatus: c.ro.live.status ?? null })}
            />
          )))}
      </section>
    );
  };

  return (
    <div className="space-y-3" data-testid="foh-workboard">
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Advisor">
          {filters.map((f) => (
            <Button
              key={f.key}
              size="sm"
              role="tab"
              aria-selected={filter === f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              className="h-9 shrink-0"
              data-testid={`foh-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ticket, customer, vehicle, VIN, plate"
            className="pl-8 h-9"
            data-testid="foh-workboard-search"
          />
        </div>
      </div>

      {filter === 'mine' && mineCount === 0 && unassignedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3 text-sm" data-testid="foh-workboard-unassigned-hint">
          <span>You have no repair orders yet. {unassignedCount} unassigned {unassignedCount === 1 ? 'one needs' : 'need'} an owner.</span>
          <Button size="sm" variant="outline" className="h-9" onClick={() => setFilter('unassigned')}>Show Unassigned</Button>
        </div>
      )}

      <div className={cn('flex flex-col gap-5 md:flex-row md:items-start md:gap-3 md:overflow-x-auto md:pb-2')}>
        {FOH_LANES.map(renderLane)}
      </div>

      <OutstandingIncompletePickups
        rows={outstanding.data ?? []}
        loading={outstanding.isLoading}
        shopTimezone={shopTimezone}
        names={names}
        onOpen={(id) => navigate(commandCenterPath(id))}
        onCheckIn={perms.canEditTickets ? (r) => setReturning({ row: r, act: 'check_in' }) : undefined}
        onSchedule={perms.canEditTickets ? (r) => setReturning({ row: r, act: 'schedule' }) : undefined}
      />
      <ReturnCheckInDialog
        open={returning?.act === 'check_in'}
        ticketId={returning?.act === 'check_in' ? returning.row.ticketId : null}
        shopId={shopId}
        subject={returning ? [returning.row.ticketNumber != null ? `RO #${returning.row.ticketNumber}` : null, returning.row.customerName].filter(Boolean).join(' · ') : null}
        onClose={() => setReturning(null)}
        onDone={(a) => {
          toast.success('Vehicle checked in', { description: a.message });
          setReturning(null);
          void notifyDayboardInvalidated({ reason: 'foh-workboard-return-check-in', shopId, ticketId: String(a.data.ticket_id ?? '') });
          void refreshBoard();
        }}
      />
      <ReturnContinuationDialog
        open={returning?.act === 'schedule'}
        ticketId={returning?.act === 'schedule' ? returning.row.ticketId : null}
        shopId={shopId}
        shopTimezone={shopTimezone}
        subject={returning ? [returning.row.ticketNumber != null ? `RO #${returning.row.ticketNumber}` : null, returning.row.customerName].filter(Boolean).join(' · ') : null}
        onClose={() => setReturning(null)}
        onDone={(a) => {
          toast.success('Remaining work scheduled', { description: a.message });
          setReturning(null);
          const day = String(a.data.scheduled_date ?? '');
          // oct20d-sched (Founder Decision 4): re-walk only the lane the continuation joined (its technician, that day),
          // never every technician's lane on that future day. An unassigned continuation joins no technician's lane.
          const contTech = a.data.technician_id ? String(a.data.technician_id) : '';
          if (day && contTech) void triggerBoardCascade(shopId, day, contTech).catch(() => undefined);
          void notifyDayboardInvalidated({ reason: 'foh-workboard-return-continuation', shopId, ticketId: String(a.data.continuation_ticket_id ?? '') });
          void refreshBoard();
        }}
      />

      <FohMoveVehicleDialog
        open={movingTicketId !== null}
        ticketId={movingTicketId}
        shopId={shopId}
        onClose={() => setMovingTicketId(null)}
        onMoved={(id, r) => {
          toast.success(r.replayed ? 'Already moved' : 'Vehicle moved', r.capacityNotVerified
            ? { description: 'Lift capacity is not on record for that bay — not verified safe.' } : undefined);
          void notifyDayboardInvalidated({ reason: 'foh-workboard-move-vehicle', shopId, ticketId: id });
          void refreshBoard();
        }}
      />
      <FohPickupConfirmDialog
        open={pickup?.kind === 'completed'}
        subject={pickup?.subject ?? null}
        whereNow={pickup?.whereNow ?? null}
        busy={pickupBusy}
        onConfirm={() => { void confirmPickup(); }}
        onCancel={() => setPickup(null)}
      />
      <IncompletePickupDialog
        open={pickup?.kind === 'incomplete'}
        subject={pickup?.subject ?? null}
        shopTimezone={shopTimezone}
        busy={pickupBusy}
        ticketId={pickup?.kind === 'incomplete' ? pickup.ticketId : null}
        onConfirm={(reason, date, ids) => { void confirmPickup(reason, date, ids); }}
        onCancel={() => setPickup(null)}
      />

      {kickBackTicketId && (
        <CustodyHandoffReasonDialog
          open
          onOpenChange={(o) => { if (!o) setKickBackTicketId(null); }}
          trigger="manual"
          eventKind="kick_to_boh"
          isPending={custody.isPending}
          onConfirm={(result) => kickBack(kickBackTicketId, result)}
        />
      )}
      {reassigning && (
        <AdvisorAssignDialog
          open
          onOpenChange={(o) => { if (!o) setReassigning(null); }}
          roLabel={formatRoNumber(reassigning.ro.owner)}
          currentAdvisorUserId={reassigning.ro.advisorUserId}
          members={members}
          pending={advisor.isPending}
          onConfirm={(target) => {
            advisor.mutateAsync({
              ticketId: reassigning.ro.id,
              mode: target ? 'reassign' : 'unassign',
              advisorUserId: target,
              expectedAdvisorUserId: reassigning.ro.advisorUserId,
            }).then(() => setReassigning(null)).catch(() => { /* hook toasts */ });
          }}
        />
      )}
    </div>
  );
}
